// src/pages/CI/GenerationReports.js
//
// Generation & PR reports - staff screen (operations, ci_admin, admin).
//   Analytics: super-admin dashboard - graphs by client / plant, daily / weekly / monthly (GenerationAnalytics.js)
//   Generate : upload a solar-portal export -> the report is built by the Python generation_service -> preview -> publish
//   Reports  : every report (draft / published), open, publish, unpublish, delete drafts
//   Monthly  : month totals per plant (published figures only)
// Clients only ever see PUBLISHED reports (in the mobile app). The report HTML itself is the original iNOVAA design.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import GenerationAnalytics from './GenerationAnalytics';
import { FileSpreadsheet, UploadCloud, Eye, CheckCircle, Trash2, RefreshCw, EyeOff, X } from 'lucide-react';

const API = process.env.REACT_APP_API_BASE_URL;

// the native option list ignores the page theme: without this it opens white-on-white
const SELECT_CSS = `.nd-select{color-scheme:dark}.nd-select option{background:#111827;color:#F1F5F9}.nd-select option:checked{background:#2563EB;color:#fff}`;

const T = {
  surface: 'var(--nd-surface)',
  border: 'var(--nd-w08)',
  text: 'var(--nd-text)',
  muted: 'var(--nd-tx35)',
  font: "'DM Sans', sans-serif",
  mono: "'DM Mono', monospace",
};

const REPORT_TYPES = [
  { v: 'GENERIC', l: 'Other / single plant' },
  { v: 'PCL', l: 'Power Cement (PCL)' },
  { v: 'PSM', l: 'PSM' },
  { v: 'BSP', l: 'Bulleh Shah Packaging (BSP)' },
  { v: 'SALITEX', l: 'Salitex (typed figures)' },
];

const SUB_PLANTS = {
  PCL: [['COMBINED', '5MW + 2MW (combined)'], ['5MW', '5MW only'], ['2MW', '2MW only']],
  PSM: [['COMBINED', '1MW + 2MW (combined)'], ['1MW', '1MW only'], ['2MW', '2MW only']],
  BSP: [['SINGLE', 'Single plant']],
  GENERIC: [['SINGLE', 'Single plant']],
  SALITEX: [['COMBINED', 'Knox + Tesla'], ['KNOX', 'Knox only'], ['TESLA', 'Tesla only']],
};

const SALITEX_FIELDS = [
  ['salitex_date', 'Report date (e.g. 21 October 2026)', 'text'],
  ['salitex_irradiance', 'Irradiance (kWh/m²)', 'number'],
  ['knox_daily', 'Knox - daily yield (kWh)', 'number'],
  ['knox_exp', 'Knox - expected (kWh)', 'number'],
  ['knox_pr', 'Knox - PR (%)', 'number'],
  ['knox_i1', 'Knox inverter 1 (kWh)', 'number'],
  ['knox_i2', 'Knox inverter 2 (kWh)', 'number'],
  ['tesla_daily', 'Tesla - daily yield (kWh)', 'number'],
  ['tesla_exp', 'Tesla - expected (kWh)', 'number'],
  ['tesla_pr', 'Tesla - PR (%)', 'number'],
  ['tesla_i1', 'Tesla inverter 1 (kWh)', 'number'],
  ['tesla_i2', 'Tesla inverter 2 (kWh)', 'number'],
];

const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 20, marginBottom: 16 };
const label = { display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.muted, marginBottom: 6 };
const input = {
  width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${T.border}`,
  background: 'var(--nd-w04)', color: T.text, fontFamily: T.font, fontSize: 13, outline: 'none',
};
const btn = (primary, disabled) => ({
  padding: '9px 16px', borderRadius: 10, border: primary ? 'none' : `1px solid ${T.border}`, cursor: disabled ? 'not-allowed' : 'pointer',
  background: primary ? 'linear-gradient(90deg,#3B82F6,#22D3A5)' : 'var(--nd-w05)', color: primary ? '#fff' : T.text,
  fontWeight: 700, fontSize: 12.5, fontFamily: T.font, opacity: disabled ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', gap: 6,
});

const errText = (e, fallback) => {
  const d = e?.response?.data;
  const msg = d?.error || d?.message || d?.detail;
  if (msg) return d?.details ? `${msg} (${d.details})` : msg;
  // no readable reason came back (a proxy / upload-limit page, an old route, a crash): at least say what did
  if (e?.response) return `${fallback} (server replied ${e.response.status}${e.response.statusText ? ` ${e.response.statusText}` : ''})`;
  return `${fallback} (no answer from the server: ${e?.message || 'network error'})`;
};
const currentMonth = () => new Date().toISOString().slice(0, 7);

export default function GenerationReports() {
  const [tab, setTab] = useState('analytics');
  const [clients, setClients] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/commercial-clients`)
      .then((r) => setClients((r.data?.data?.clients || []).filter((c) => !c.is_epc)))
      .catch(() => {});
  }, []);

  return (
    <div style={{ padding: '14px 20px', fontFamily: T.font, color: T.text }}>
      <style>{SELECT_CSS}</style>
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 12 }}>
        <FileSpreadsheet size={22} color="#3B82F6" />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.18em' }}>iNOVAA · C&I MANAGEMENT</div>
          <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.03em' }}>Generation &amp; PR Reports</div>
        </div>
        <button
          style={{ ...btn(false, false), whiteSpace: 'nowrap' }}
          title="Sends you a hello from iNovaa Ai in the Team chat (ci_admin / admin)"
          onClick={() => axios.post(`${API}/generation/alerts/test`)
            .then((r) => alert(r.data?.message || 'Sent.'))
            .catch((e) => alert(errText(e, 'Could not send the test message.')))}
        >
          🔔 Test iNovaa Ai
        </button>
        {[['analytics', 'Analytics'], ['generate', 'Generate'], ['reports', 'Reports'], ['monthly', 'Monthly summary']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ ...btn(tab === k, false) }}>{l}</button>
        ))}
      </div>

      {tab === 'analytics' && <GenerationAnalytics clients={clients} />}
      {tab === 'generate' && <GenerateTab clients={clients} onSaved={() => setTab('reports')} />}
      {tab === 'reports' && <ReportsTab clients={clients} />}
      {tab === 'monthly' && <MonthlyTab clients={clients} />}
    </div>
  );
}

/* ---------------------------------------------------------------- Generate */

function GenerateTab({ clients, onSaved }) {
  const [clientId, setClientId] = useState('');
  const [reportCode, setReportCode] = useState('GENERIC');
  const [subPlant, setSubPlant] = useState('SINGLE');
  const [eqFormula, setEqFormula] = useState('installed_div_yield');
  const [dateFilter, setDateFilter] = useState('');
  const [cap1, setCap1] = useState('');
  const [cap2, setCap2] = useState('');
  const [files, setFiles] = useState({ main_file: null, irr_file: null, second_file: null });
  const [sal, setSal] = useState({});
  const [graphs, setGraphs] = useState({ knox_g1: null, knox_g2: null });
  const [notes, setNotes] = useState({ knox_i1_note: '', knox_i2_note: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const subs = SUB_PLANTS[reportCode] || SUB_PLANTS.GENERIC;
  const combined = subPlant === 'COMBINED' && (reportCode === 'PCL' || reportCode === 'PSM');
  // Salitex: which plant(s) this report is for decides which figures are asked for
  const wantKnox = reportCode === 'SALITEX' && (subPlant === 'COMBINED' || subPlant === 'KNOX');
  const wantTesla = reportCode === 'SALITEX' && (subPlant === 'COMBINED' || subPlant === 'TESLA');
  const salFields = SALITEX_FIELDS.filter(([k]) => (k.startsWith('knox_') ? wantKnox : k.startsWith('tesla_') ? wantTesla : true));

  const onTypeChange = (code) => {
    setReportCode(code);
    setSubPlant((SUB_PLANTS[code] || SUB_PLANTS.GENERIC)[0][0]);
    setResult(null);
  };

  const submit = async () => {
    setError('');
    if (!clientId) return setError('Pick the client this report is for.');
    if (reportCode !== 'SALITEX' && !files.main_file) return setError('Choose the main Excel/CSV file.');
    if (combined && !files.second_file) return setError('A combined report needs the second plant\'s file too.');
    if (reportCode === 'SALITEX') {
      const missing = salFields.find(([k]) => String(sal[k] ?? '').trim() === '');
      if (missing) return setError(`${missing[1]} is required.`);
    }
    const fd = new FormData();
    fd.append('client_id', clientId);
    fd.append('report_code', reportCode);
    fd.append('sub_plant', subPlant);
    fd.append('eq_hours_formula', eqFormula);
    fd.append('single_date_filter', dateFilter);
    fd.append('custom_capacity_kwp', cap1);
    fd.append('custom_capacity_kwp_2', cap2);
    if (reportCode === 'SALITEX') {
      // only the figures for the chosen plant(s); stale values typed before switching plant are not sent
      salFields.forEach(([k]) => fd.append(k, sal[k]));
      if (wantKnox) {
        Object.entries(graphs).forEach(([k, f]) => f && fd.append(k, f));
        Object.entries(notes).forEach(([k, v]) => v.trim() && fd.append(k, v.trim()));
      }
    }
    else Object.entries(files).forEach(([k, f]) => f && fd.append(k, f));
    try {
      setBusy(true);
      const r = await axios.post(`${API}/generation/generate`, fd, { timeout: 180000 });
      setResult(r.data);
    } catch (e) {
      setError(errText(e, 'Could not build the report.'));
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    try {
      await axios.post(`${API}/generation/reports/${result.report_id}/publish`);
      alert('Published - the client can now see this report.');
      setResult(null);
      onSaved();
    } catch (e) {
      alert(errText(e, 'Could not publish.'));
    }
  };

  const discard = async () => {
    if (!window.confirm('Delete this draft?')) return;
    try {
      await axios.delete(`${API}/generation/reports/${result.report_id}`);
      setResult(null);
    } catch (e) {
      alert(errText(e, 'Could not delete.'));
    }
  };

  return (
    <>
      <div style={card}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 14 }}>
          <div>
            <label style={label}>Client *</label>
            <select className="nd-select" style={input} value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">Select client…</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.client_name}</option>)}
            </select>
          </div>
          <div>
            <label style={label}>Report type *</label>
            <select className="nd-select" style={input} value={reportCode} onChange={(e) => onTypeChange(e.target.value)}>
              {REPORT_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </div>
          <div>
            <label style={label}>Plant(s)</label>
            <select className="nd-select" style={input} value={subPlant} onChange={(e) => setSubPlant(e.target.value)}>
              {subs.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>

        {reportCode === 'SALITEX' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 14, marginTop: 16 }}>
            {salFields.map(([k, l, t]) => (
              <div key={k}>
                <label style={label}>{l} *</label>
                <input style={input} type={t} step="any" value={sal[k] || ''} onChange={(e) => setSal((p) => ({ ...p, [k]: e.target.value }))} />
              </div>
            ))}
            {wantKnox && (
              <>
                <FileField accept="image/png,image/jpeg,image/webp" text="KNOX Inverter 1 (Left) — Production Curve picture" file={graphs.knox_g1} onFile={(f) => setGraphs((p) => ({ ...p, knox_g1: f }))} />
                <FileField accept="image/png,image/jpeg,image/webp" text="KNOX Inverter 2 (Right) — Production Curve picture" file={graphs.knox_g2} onFile={(f) => setGraphs((p) => ({ ...p, knox_g2: f }))} />
                <div>
                  <label style={label}>Inverter 1 — site note (optional)</label>
                  <input style={input} type="text" maxLength={400} placeholder="e.g. dip at 11:50 - production line load changed" value={notes.knox_i1_note} onChange={(e) => setNotes((p) => ({ ...p, knox_i1_note: e.target.value }))} />
                </div>
                <div>
                  <label style={label}>Inverter 2 — site note (optional)</label>
                  <input style={input} type="text" maxLength={400} placeholder="shown in the report exactly as typed" value={notes.knox_i2_note} onChange={(e) => setNotes((p) => ({ ...p, knox_i2_note: e.target.value }))} />
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 14, marginTop: 16 }}>
              <FileField text={combined ? 'First plant file (Excel / CSV) *' : 'Main file (Excel / CSV) *'} file={files.main_file} onFile={(f) => setFiles((p) => ({ ...p, main_file: f }))} />
              {combined && <FileField text="Second plant file *" file={files.second_file} onFile={(f) => setFiles((p) => ({ ...p, second_file: f }))} />}
              <FileField text="Irradiance file (optional)" file={files.irr_file} onFile={(f) => setFiles((p) => ({ ...p, irr_file: f }))} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 14, marginTop: 16 }}>
              <div>
                <label style={label}>Capacity override (kWp)</label>
                <input style={input} type="number" step="any" placeholder="only if the file has none" value={cap1} onChange={(e) => setCap1(e.target.value)} />
              </div>
              {combined && (
                <div>
                  <label style={label}>Second plant capacity (kWp)</label>
                  <input style={input} type="number" step="any" value={cap2} onChange={(e) => setCap2(e.target.value)} />
                </div>
              )}
              <div>
                <label style={label}>Only this date (optional)</label>
                <input style={input} type="text" placeholder="e.g. 2026-10-21" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} />
              </div>
              <div>
                <label style={label}>Equivalent hours formula</label>
                <select className="nd-select" style={input} value={eqFormula} onChange={(e) => setEqFormula(e.target.value)}>
                  <option value="installed_div_yield">Installed Power / PV Yield</option>
                  <option value="yield_div_installed">PV Yield / Installed Power</option>
                </select>
              </div>
            </div>
          </>
        )}

        {error && <div style={{ marginTop: 14, color: '#F87171', fontSize: 13, fontWeight: 600 }}>⚠ {error}</div>}
        <div style={{ marginTop: 18 }}>
          <button style={btn(true, busy)} disabled={busy} onClick={submit}>
            <UploadCloud size={15} /> {busy ? 'Building report…' : 'Generate report'}
          </button>
        </div>
      </div>

      {result && (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontWeight: 800, fontSize: 15 }}>{result.title}</div>
              <div style={{ fontSize: 12, color: T.muted }}>{result.period} · saved as a <b>draft</b> (clients cannot see it yet)</div>
            </div>
            <button style={btn(false, false)} onClick={discard}><Trash2 size={14} /> Delete draft</button>
            <button style={btn(true, false)} onClick={publish}><CheckCircle size={14} /> Publish to client</button>
          </div>
          {result.duplicate_of && (
            <div style={{ fontSize: 12, color: '#FBBF24', marginBottom: 8 }}>
              ⚠ The same file was already used in report #{result.duplicate_of}. Publish only one of them.
            </div>
          )}
          {(result.warnings || []).map((w, i) => (
            <div key={i} style={{ fontSize: 12, color: '#FBBF24', marginBottom: 6 }}>⚠ {w}</div>
          ))}
          <ReportFrame html={result.html} />
        </div>
      )}
    </>
  );
}

function FileField({ text, file, onFile, accept = '.xlsx,.xls,.csv' }) {
  return (
    <div>
      <label style={label}>{text}</label>
      <input style={input} type="file" accept={accept} onChange={(e) => onFile(e.target.files?.[0] || null)} />
      {file && <div style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>{file.name}</div>}
    </div>
  );
}

// sandbox: scripts run (the report has Print / Download buttons) but with no access to this page, its cookies or token.
function ReportFrame({ html }) {
  return (
    <iframe
      title="Report preview"
      srcDoc={html}
      sandbox="allow-scripts allow-modals allow-downloads"
      style={{ width: '100%', height: '80vh', border: `1px solid ${T.border}`, borderRadius: 12, background: '#F2F2F7' }}
    />
  );
}

/* ---------------------------------------------------------------- Reports */

function ReportsTab({ clients }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientId, setClientId] = useState('');
  const [status, setStatus] = useState('');
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/generation/reports`, { params: { client_id: clientId || undefined, status: status || undefined } });
      setRows(r.data.reports || []);
    } catch (e) {
      alert(errText(e, 'Could not load reports.'));
    } finally {
      setLoading(false);
    }
  }, [clientId, status]);

  useEffect(() => { load(); }, [load]);

  const view = async (id) => {
    try {
      const r = await axios.get(`${API}/generation/reports/${id}`);
      setOpen(r.data.report);
    } catch (e) {
      alert(errText(e, 'Could not open the report.'));
    }
  };

  const act = async (id, action) => {
    try {
      if (action === 'delete') {
        if (!window.confirm('Delete this draft?')) return;
        await axios.delete(`${API}/generation/reports/${id}`);
      } else {
        await axios.post(`${API}/generation/reports/${id}/${action}`);
      }
      load();
    } catch (e) {
      alert(errText(e, 'That did not work.'));
    }
  };

  const th = { textAlign: 'left', padding: '10px 12px', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.muted, borderBottom: `1px solid ${T.border}` };
  const td = { padding: '10px 12px', borderBottom: `1px solid var(--nd-w04)`, fontSize: 13 };

  return (
    <div style={card}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        <select className="nd-select" style={{ ...input, width: 220 }} value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">All clients</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.client_name}</option>)}
        </select>
        <select className="nd-select" style={{ ...input, width: 160 }} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Draft + published</option>
          <option value="draft">Drafts</option>
          <option value="published">Published</option>
        </select>
        <button style={btn(false, false)} onClick={load}><RefreshCw size={14} /> Refresh</button>
      </div>
      {loading ? <div style={{ color: T.muted }}>Loading…</div> : rows.length === 0 ? (
        <div style={{ color: T.muted }}>No reports yet.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
            <thead><tr>
              <th style={th}>Client</th><th style={th}>Report</th><th style={th}>Period</th><th style={th}>Status</th><th style={th}>Made by</th><th style={{ ...th, textAlign: 'right' }}>Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td style={td}>{r.client_name}</td>
                  <td style={td}><b>{r.title}</b><div style={{ fontSize: 11, color: T.muted }}>#{r.id}</div></td>
                  <td style={{ ...td, fontFamily: T.mono, fontSize: 12 }}>{r.period_label || '—'}</td>
                  <td style={td}>
                    <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700,
                      background: r.status === 'published' ? 'rgba(34,211,165,0.14)' : 'rgba(251,191,36,0.14)',
                      color: r.status === 'published' ? '#22D3A5' : '#FBBF24' }}>{r.status}</span>
                  </td>
                  <td style={td}>{r.created_by_name || '—'}</td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button style={btn(false, false)} onClick={() => view(r.id)}><Eye size={13} /> View</button>{' '}
                    {r.status === 'draft' ? (
                      <>
                        <button style={btn(true, false)} onClick={() => act(r.id, 'publish')}><CheckCircle size={13} /> Publish</button>{' '}
                        <button style={btn(false, false)} onClick={() => act(r.id, 'delete')}><Trash2 size={13} /></button>
                      </>
                    ) : (
                      <button style={btn(false, false)} onClick={() => act(r.id, 'unpublish')}><EyeOff size={13} /> Unpublish</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 10000, padding: 20, display: 'flex' }}>
          <div style={{ background: T.surface, borderRadius: 16, width: '100%', maxWidth: 1100, margin: 'auto', padding: 16, maxHeight: '95vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ flex: 1, fontWeight: 800 }}>{open.client_name} · {open.title}</div>
              <button style={btn(false, false)} onClick={() => setOpen(null)}><X size={14} /> Close</button>
            </div>
            <ReportFrame html={open.html} />
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- Monthly */

function MonthlyTab({ clients }) {
  const [month, setMonth] = useState(currentMonth());
  const [clientId, setClientId] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/generation/summary`, { params: { month, client_id: clientId || undefined } })
      .then((r) => alive && setRows(r.data.summary || []))
      .catch((e) => alive && alert(errText(e, 'Could not load the summary.')))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [month, clientId]);

  const totals = useMemo(() => rows.reduce((a, r) => a + Number(r.total_yield_kwh || 0), 0), [rows]);
  const th = { textAlign: 'left', padding: '10px 12px', fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.muted, borderBottom: `1px solid ${T.border}` };
  const td = { padding: '10px 12px', borderBottom: `1px solid var(--nd-w04)`, fontSize: 13 };

  return (
    <div style={card}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        <input style={{ ...input, width: 170 }} type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        <select className="nd-select" style={{ ...input, width: 220 }} value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">All clients</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.client_name}</option>)}
        </select>
        <div style={{ marginLeft: 'auto', fontSize: 12, color: T.muted }}>
          Published reports only · total <b style={{ color: T.text }}>{totals.toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh</b>
        </div>
      </div>
      {loading ? <div style={{ color: T.muted }}>Loading…</div> : rows.length === 0 ? (
        <div style={{ color: T.muted }}>No published data for this month.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
            <thead><tr>
              <th style={th}>Client</th><th style={th}>Plant</th><th style={th}>Days</th><th style={th}>Offline</th>
              <th style={th}>Generation (kWh)</th><th style={th}>Avg PR</th><th style={th}>Avg eq. hours</th><th style={th}>Best day (kWh)</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => {
                const pr = r.avg_pr_pct == null ? null : Number(r.avg_pr_pct);
                return (
                  <tr key={`${r.plant_id}-${r.month}`}>
                    <td style={td}>{r.client_name}</td>
                    <td style={td}><b>{r.plant_name}</b></td>
                    <td style={td}>{r.days_reported}</td>
                    <td style={{ ...td, color: r.days_offline > 0 ? '#F87171' : T.text }}>{r.days_offline}</td>
                    <td style={{ ...td, fontFamily: T.mono }}>{Number(r.total_yield_kwh || 0).toLocaleString()}</td>
                    <td style={{ ...td, fontWeight: 700, color: pr == null ? T.muted : pr >= 80 ? '#22D3A5' : '#FBBF24' }}>
                      {pr == null ? '—' : `${pr.toFixed(1)}%`}
                    </td>
                    <td style={{ ...td, fontFamily: T.mono }}>{r.avg_eq_hours == null ? '—' : Number(r.avg_eq_hours).toFixed(2)}</td>
                    <td style={{ ...td, fontFamily: T.mono }}>{r.best_day_kwh == null ? '—' : Number(r.best_day_kwh).toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
